import React from 'react';
import { useIntl } from 'react-intl';
import { COUNT_BADGE_TONE_ERROR, CountBadge, Tabs } from '@aibuild/ui';
import {
  BUILDER_PANE_APP,
  BUILDER_PANE_CHAT,
  BUILDER_PANE_DETAILS,
  type BuilderPane,
} from './builderWorkspace.constants';
import { TabsBar } from './BuilderPaneTabs.styled';

const PANE_LABELS: Readonly<Record<BuilderPane, { id: string; defaultMessage: string }>> = {
  [BUILDER_PANE_DETAILS]: { id: 'apps.builder.pane.details', defaultMessage: 'Details' },
  [BUILDER_PANE_APP]: { id: 'apps.builder.pane.app', defaultMessage: 'App' },
  [BUILDER_PANE_CHAT]: { id: 'apps.builder.pane.chat', defaultMessage: 'Chat' },
};

const PANES: readonly BuilderPane[] = [BUILDER_PANE_DETAILS, BUILDER_PANE_APP, BUILDER_PANE_CHAT];

interface BuilderPaneTabsProps {
  readonly pane: BuilderPane;
  readonly onSelect: (pane: BuilderPane) => void;
  /** Steps waiting for approval, which only the App pane shows. */
  readonly appApprovals?: number;
}

const BuilderPaneTabs: React.FC<BuilderPaneTabsProps> = ({ pane, onSelect, appApprovals = 0 }) => {
  const intl = useIntl();
  return (
    <TabsBar>
      <Tabs
        ariaLabel={intl.formatMessage({ id: 'apps.builder.pane.tabs', defaultMessage: 'Builder panes' })}
        dataTestId="app-builder__panes"
      >
        {PANES.map((candidate) => (
          <Tabs.Tab
            key={candidate}
            active={pane === candidate}
            onClick={() => onSelect(candidate)}
            dataTestId={`app-builder__pane-${candidate}`}
          >
            {intl.formatMessage(PANE_LABELS[candidate])}
            {candidate === BUILDER_PANE_APP && appApprovals > 0 && (
              <CountBadge
                tone={COUNT_BADGE_TONE_ERROR}
                ariaLabel={intl.formatMessage(
                  { id: 'apps.builder.pane.approvals', defaultMessage: '{count, plural, one {# step} other {# steps}} to approve' },
                  { count: appApprovals },
                )}
                dataTestId="app-builder__pane-app-approvals"
              >
                {appApprovals}
              </CountBadge>
            )}
          </Tabs.Tab>
        ))}
      </Tabs>
    </TabsBar>
  );
};

export default BuilderPaneTabs;
